import type {
  PracticeMode,
  PracticeScope,
  SessionConfig,
  SessionOutcome,
} from '@/lib/practice/session-types';

/** What a writer hands `useRecordPracticeSession().mutate` — structurally
 *  its `NewSessionPayload`, restated here because lib/ may not import hooks. */
export interface SessionRecordPayload {
  clientSessionId: string;
  mode: PracticeMode;
  scope: PracticeScope;
  total: number;
  incomplete: boolean;
  mastered: number;
  improved: number;
  weak: number;
  durationMs: number;
  outcomes: SessionOutcome[];
}

/**
 * The history record for a practice run — the ONE place `total` and
 * `incomplete` are decided, shared by every writer: the finish, the
 * mid-session partial flush and the runner-crash flush.
 *
 * `total` is the deck size (what the runner froze once its queue loaded), so
 * a partial reads "Practiced 1 of 3" rather than "1 of 1". It is floored at
 * the rated count, so a deck size that never reached the orchestrator
 * cannot produce "2 of 0". `incomplete` is "fewer rated than the deck".
 * The grade buckets are over the rated outcomes only.
 */
export function buildSessionRecord({
  clientSessionId,
  config,
  outcomes,
  deckSize,
  durationMs,
}: {
  clientSessionId: string;
  config: SessionConfig;
  outcomes: SessionOutcome[];
  deckSize: number;
  durationMs: number;
}): SessionRecordPayload {
  const total = Math.max(deckSize, outcomes.length);
  return {
    clientSessionId,
    mode: config.mode,
    scope: config.scope,
    total,
    incomplete: outcomes.length < total,
    mastered: outcomes.filter(o => o.grade === 4).length,
    improved: outcomes.filter(o => o.grade === 3).length,
    weak: outcomes.filter(o => o.grade <= 2).length,
    durationMs,
    outcomes,
  };
}
